import { Form, spinner } from '@/components';
import { specialRequestsHttp } from '@/core';
import { openFileInNewWindow } from '@/utils';
import { useHomeLookupSource } from '@/views';
import { FilePdfOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import i18next from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

interface Props {
  communityId: string;
  projectIds?: string[];
  homeIds?: string[];
}

const schema = Yup.object()
  .shape({
    homeId: Yup.string().required(),
    includeInactive: Yup.boolean().required(),
    includeActive: Yup.boolean().required(),
  })
  .test(
    'at-least-one-selected',
    'At least one option must be selected',
    function (values) {
      if (values.includeActive || values.includeInactive) {
        return true;
      }

      return this.createError({
        path: 'includeInactive',
        message: i18next.t<string>(
          'pricing.specialRequest.consolidatedPdf.errors.oneRequired',
        ),
      });
    },
  );

type FormValue = OmitIndex<Yup.InferType<typeof schema>>;

export function DownloadConsolidatedSpecialRequestPdfButton(props: Props) {
  const { communityId, projectIds, homeIds } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'pricing.specialRequest.consolidatedPdf',
  });
  const [open, setOpen] = useState(false);

  const homesDataSource = useHomeLookupSource(communityId, projectIds, homeIds);

  const initialValues = useMemo<FormValue>(
    () => ({
      homeId: homeIds?.length === 1 ? homeIds[0] : '',
      includeActive: true,
      includeInactive: false,
    }),
    [homeIds],
  );

  return (
    <>
      <Button icon={<FilePdfOutlined />} onClick={() => setOpen(true)}>
        {t('button')}
      </Button>

      <Modal
        visible={open}
        title={t('title')}
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setOpen(false)}>
            {t('cancel')}
          </Button>,
          <Form.Submit
            key="download"
            uid="download-consolidated-special-request-pdf"
            type="primary"
            icon={<FilePdfOutlined />}
          >
            {t('ok')}
          </Form.Submit>,
        ]}
      >
        <Form.Formik<FormValue>
          uid="download-consolidated-special-request-pdf"
          i18n="pricing.specialRequest.consolidatedPdf"
          initialValues={initialValues}
          validationSchema={schema}
          enableReinitialize
          onSubmit={(values) => {
            spinner.show();
            specialRequestsHttp
              .downloadConsolidatedPdf({
                homeId: values.homeId,
                includeInactive: values.includeInactive,
                includeActive: values.includeActive,
              })
              .then((file) => {
                openFileInNewWindow(file);
                setOpen(false);
              })
              .finally(() => {
                spinner.hide();
              });
          }}
        >
          <>
            <Form.AsyncSelect
              key={homeIds?.toString()}
              name="homeId"
              dataSource={homesDataSource}
              preload
              required
            />
            <Form.Checkbox name="includeActive" label={t('includeActive')} />
            <Form.Checkbox
              name="includeInactive"
              label={t('includeInactive')}
            />
          </>
        </Form.Formik>
      </Modal>
    </>
  );
}
